import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { locationApi } from 'components/location/locationApi';
import { Location, LocationService, LocationsSummary } from 'components/location/model/Location';
import logger from 'lib/logger';

const LOCATIONS_LIMIT = '25';

export interface LocationsState {
    locations: Location[];
    page: LocationsSummary;
    error: string;
    isLoading: boolean;
    isNextPageLoading?: boolean;
    lat?: number;
    lng?: number;
    currentLocation?: Location;
    isCurrentLocationLoading: boolean;
}

export const initialState: LocationsState = {
    locations: [],
    error: '',
    page: null,
    isLoading: true,
    isNextPageLoading: false,
    currentLocation: undefined,
    isCurrentLocationLoading: true
};

export interface GetLocationsOptions {
    coords?: { lat: number; long: number };
    searchTerm?: string;
    services?: LocationService | LocationService[];
}

function buildQueryParams(options?: GetLocationsOptions, startKey?: string): Record<string, string> {
    const queryParameters: Record<string, string> = {
        limit: LOCATIONS_LIMIT,
        state: 'ACTIVE',
        services: 'pre_order'
    };
    if (startKey) {
        queryParameters.startKey = startKey;
    }
    if (options?.coords) {
        queryParameters.lat = String(options.coords.lat);
        queryParameters.long = String(options.coords.long);
    }
    if (options?.searchTerm) {
        queryParameters.search = options.searchTerm;
    }
    if (options?.services) {
        queryParameters.services = Array.isArray(options.services)
            ? options.services.reduce((acc, service) => (acc += `${service},`), '').replace(/,\s*$/, '')
            : options.services;
    }
    return queryParameters;
}

export const getLocations = createAsyncThunk(
    'locations/getLocations',
    async (options?: GetLocationsOptions) => {
        const queryParameters = buildQueryParams(options);
        const response = await locationApi.getList(queryParameters);
        return { locations: response.items, page: response.page };
    }
);

export const getAllLocations = createAsyncThunk('locations/getAllLocations', async () => {
    const response = await locationApi.getList({ state: 'ACTIVE' });
    return { locations: response.items, page: response.page };
});

export const loadLocations = createAsyncThunk(
    'locations/loadLocations',
    async ({ page, options }: { page: LocationsSummary; options?: GetLocationsOptions }) => {
        if (!page || !page.nextKey) {
            return null; // no more pages
        }
        const queryParameters = buildQueryParams(options, page.nextKey);
        const response = await locationApi.getList(queryParameters);
        return { locations: response.items, page: response.page };
    }
);

export const getCurrentLocation = createAsyncThunk(
    'locations/getCurrentLocation',
    async (locationId: string) => {
        const currentLocation = await locationApi.getLocation(locationId);
        return currentLocation;
    }
);

const locationsSlice = createSlice({
    name: 'locations',
    initialState,
    reducers: {
        setCoordinates(state, action: PayloadAction<{ lat: number; lng: number }>) {
            state.lat = action.payload.lat;
            state.lng = action.payload.lng;
        },
        resetCurrentLocation(state) {
            state.isCurrentLocationLoading = true;
            state.currentLocation = undefined;
            state.error = '';
        }
    },
    extraReducers: builder => {
        builder
            // getLocations
            .addCase(getLocations.pending, state => {
                state.isLoading = true;
            })
            .addCase(getLocations.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = '';
                state.locations = action.payload.locations as any;
                state.page = action.payload.page;
            })
            .addCase(getLocations.rejected, (state, action) => {
                state.isLoading = false;
                state.locations = [];
                state.page = null;
                state.error = action.error.message ?? '';
                logger.error(action.error);
            })
            // getAllLocations
            .addCase(getAllLocations.pending, state => {
                state.isLoading = true;
            })
            .addCase(getAllLocations.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = '';
                state.locations = action.payload.locations as any;
                state.page = action.payload.page;
            })
            .addCase(getAllLocations.rejected, (state, action) => {
                state.isLoading = false;
                state.locations = [];
                state.page = null;
                state.error = action.error.message ?? '';
                logger.error(action.error);
            })
            // loadLocations (pagination)
            .addCase(loadLocations.pending, state => {
                state.isNextPageLoading = true;
            })
            .addCase(loadLocations.fulfilled, (state, action) => {
                state.isNextPageLoading = false;
                if (action.payload) {
                    state.locations = [...state.locations, ...action.payload.locations] as any;
                    state.page = action.payload.page;
                    state.error = '';
                }
            })
            .addCase(loadLocations.rejected, (state, action) => {
                state.isNextPageLoading = false;
                state.locations = [];
                state.page = null;
                state.error = action.error.message ?? '';
                logger.error(action.error);
            })
            // getCurrentLocation
            .addCase(getCurrentLocation.pending, state => {
                state.isCurrentLocationLoading = true;
                state.currentLocation = undefined;
                state.error = '';
            })
            .addCase(getCurrentLocation.fulfilled, (state, action) => {
                state.isCurrentLocationLoading = false;
                state.currentLocation = action.payload as any;
                state.error = '';
            })
            .addCase(getCurrentLocation.rejected, (state, action) => {
                state.isCurrentLocationLoading = false;
                state.currentLocation = undefined;
                state.error = action.error.message ?? '';
                logger.error(action.error);
            });
    }
});

export const { setCoordinates, resetCurrentLocation } = locationsSlice.actions;
export default locationsSlice.reducer;
